import { useEffect, useState } from 'react';
import { useAuth } from '@workos-inc/authkit-react';
import LoadingPopup from '@/shared/components/LoadingPopup/LoadingPopup';
import { AttorneyPage as Attorney } from './features/attorney';
import { AdminPage as Admin } from './features/admin';
import { hasAnyAdminAccess } from '@/shared/config/adminPermissions';
import { logger } from '@/shared/utils/logger';

type AppView = 'admin' | 'attorney';

export default function App() {
  const { isLoading, user, signIn } = useAuth();
  const [currentView, setCurrentView] = useState<AppView | null>(null);

  // Automatically sign in the user if they're not authenticated
  useEffect(() => {
    if (!user && !isLoading) {
      signIn(); // Automatically triggers the sign-in flow
    }
  }, [user, isLoading, signIn]);

  // Set user context for Sentry error tracking
  useEffect(() => {
    if (user) {
      logger.setUser({
        id: user.id,
        email: user.email,
        username: user.firstName && user.lastName
          ? `${user.firstName} ${user.lastName}`
          : user.email,
      });
    }
  }, [user]);

  // Determine initial view based on admin status
  useEffect(() => {
    if (user && currentView === null) {
      const isAdmin = hasAnyAdminAccess(user.email);
      // Default to admin view if user has admin access, otherwise attorney
      setCurrentView(isAdmin ? 'admin' : 'attorney');
    }
  }, [user, currentView]);

  // Show a loading spinner while checking user state or during sign-in
  if (isLoading || !user || currentView === null) {
    return <LoadingPopup show={true} />;
  }

  // Render based on current view
  if (currentView === 'admin') {
    return (
      <Admin
        onSwitchToAttorney={() => setCurrentView('attorney')}
      />
    );
  }

  return (
    <Attorney
      onSwitchToAdmin={
        hasAnyAdminAccess(user.email)
          ? () => setCurrentView('admin')
          : undefined
      }
    />
  );
}
